import {Link, useCart, Image, ClientAnalytics} from '@shopify/hydrogen';
import clsx from 'clsx';

import {PageSectionContainer} from '~/components';
import {SearchInputContainer} from '../algolia/HeaderSearchInput';

import {CartDrawer} from './CartDrawer.client';
import {MenuDrawer} from './MenuDrawer.client';
import {useDrawer} from '~/components';

import SanityImage from '../media/SanityImage.client';
import iconCart from '~/assets/icons/icon-cart.svg';
import iconProfile from '~/assets/icons/icon-profile.svg';
import iconMenu from '~/assets/icons/icon-menu.svg';
import {HeaderEntity, MenuLinkEntity, TextInternalEntity} from '~/types';
import {TextInternal} from '../elements';
import sanityConfig from '../../../sanity.config';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useCartDrawer} from '~/components/contexts/CartDrawerProvider.client';
import {Product} from '@shopify/hydrogen/storefront-api-types';
import {
  ANALYTIC_EVENT_NAMES,
  DATA_TEST_IDS,
  ELEMENT_IDS,
  SITE_NAME,
} from '~/lib/const';
import {useProductCartItems} from '~/hooks/useProductCartItems';

let timeoutID: NodeJS.Timeout | null = null;

function HeaderTextInternal(props: TextInternalEntity) {
  const {variant} = props;
  let className: string | undefined;
  // span is the default one for
  if (variant === 'span') {
    className +=
      'text-[15px] md:text-[15px] leading-5 tracking-wider font-bold font-header';
  }

  return <TextInternal className={className} {...props} />;
}
/**
 * A client component that specifies the content of the header on the website
 */
export function Header({
  title,
  data,
  logo,
  searchInputPlaceholder,
  featuredProducts,
}: {
  title: string;
  data?: HeaderEntity;
  logo?: any;
  searchInputPlaceholder?: string;
  featuredProducts?: Product[];
}) {
  const {menuLinks} = data ?? {};
  const {
    isOpen: isCartOpen,
    openDrawer: openCart,
    closeDrawer: closeCart,
  } = useCartDrawer();

  const {
    isOpen: isMenuOpen,
    openDrawer: openMenu,
    closeDrawer: closeMenu,
  } = useDrawer();

  return (
    <div className={'bg-white sticky top-0 z-40'}>
      <PageSectionContainer disableMarginY>
        <CartDrawer isOpen={isCartOpen} onClose={closeCart} />
        {menuLinks && (
          <MenuDrawer
            isOpen={isMenuOpen}
            onClose={closeMenu}
            menuLinks={menuLinks}
          />
        )}
        <DesktopHeader
          title={title}
          logo={logo}
          searchInputPlaceholder={searchInputPlaceholder}
          featuredProducts={featuredProducts}
          menuLinks={menuLinks}
          openCart={openCart}
          openMenu={openMenu}
        />
      </PageSectionContainer>

      <div id={ELEMENT_IDS.PRODUCT_STICKY_HEADER}></div>
    </div>
  );
}

function DesktopHeader({
  title,
  logo,
  menuLinks,
  searchInputPlaceholder,
  featuredProducts,
  openCart,
  openMenu,
}: {
  title: string;
  logo: any;
  searchInputPlaceholder?: string;
  menuLinks?: MenuLinkEntity[];
  featuredProducts?: Product[];
  openCart: () => void;
  openMenu: () => void;
}) {
  const styles = {
    button: 'relative flex items-center justify-center h-[50px]',
    container: `lg:flex flex-col transition duration-300 z-[999] justify-between w-full leading-none`,
  };

  const [activeMenu, setActiveMenu] = useState<MenuLinkEntity | null>(null);

  const onMouseEnter = useCallback(
    (item: MenuLinkEntity) => {
      if (activeMenu && item.megaMenuLinks) {
        setActiveMenu(item);
      } else {
        timeoutID = setTimeout(() => {
          setActiveMenu(item.megaMenuLinks ? item : null);
        }, 300);
      }
    },
    [activeMenu, setActiveMenu],
  );

  const onMouseLeave = useCallback(() => {
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
  }, []);

  const onClick = useCallback(
    (name: string, url: string) => {
      ClientAnalytics.publish(ANALYTIC_EVENT_NAMES.NAVIGATION_CLICK, true, {
        event: {
          _clear: true,
          event: ANALYTIC_EVENT_NAMES.NAVIGATION_CLICK,
          navigation: {
            navigation_selection: name,
            navigation_click_path: url,
          },
        },
      });

      setActiveMenu(null);
    },
    [setActiveMenu],
  );

  useEffect(() => {
    activeMenu
      ? (document.body.style.overflowX = 'hidden')
      : (document.body.style.overflowX = 'auto');
  }, [activeMenu]);

  return (
    <header role="banner" className={styles.container} id={ELEMENT_IDS.HEADER}>
      <div className="flex items-center justify-between h-[70px]">
        <div className="flex items-center justify-start w-[200px]">
          <Link className={`font-bold`} to="/">
            {logo ? (
              <SanityImage
                alt={logo?.altText ?? SITE_NAME}
                crop={logo?.crop}
                hotspot={logo?.hotspot}
                layout="responsive"
                objectFit="contain"
                sizes="5vw"
                src={logo?.asset._ref}
                className={'h-[40px] xl:h-[46px] object-contain object-left'}
                breakpoints={[
                  [140, 40],
                  [160, 46],
                ]}
              />
            ) : (
              title
            )}
          </Link>
        </div>
        <div className={'hidden xl:flex'}>
          <SearchInputContainer
            placeholder={searchInputPlaceholder}
            featuredProducts={featuredProducts}
          />
        </div>
        <div className="flex items-center justify-end w-[200px]">
          <Link
            data-testid={DATA_TEST_IDS.LINK('account')}
            to={'/account'}
            className={clsx(styles.button, 'px-2.5 xl:px-[17px]')}
          >
            <Image
              loading="lazy"
              src={iconProfile}
              width="26px"
              height="26px"
              alt="go to account page"
            />
          </Link>
          <button
            data-testid={DATA_TEST_IDS.BUTTON('cart')}
            type={'button'}
            aria-label={'open cart drawer'}
            onClick={openCart}
            className={clsx(styles.button, 'px-2.5 xl:pl-[17px] xl:pr-[7px]')}
          >
            <Image
              loading="lazy"
              src={iconCart}
              width="26px"
              height="26px"
              alt="open cart"
            />
            <CartBadge className={'top-[4px] right-[5px]'} />
          </button>
          <button
            data-testid={DATA_TEST_IDS.BUTTON('menu')}
            type="button"
            aria-label={'open menu'}
            className={clsx(styles.button, '-mr-2.5 px-2.5 block xl:hidden')}
            onClick={openMenu}
          >
            <Image
              loading="lazy"
              src={iconMenu}
              width="26px"
              height="26px"
              alt="open menu"
            />
          </button>
        </div>
      </div>
      <div
        className={'min-h-[60px] items-center justify-center hidden xl:flex'}
      >
        <nav className="flex flex-wrap gap-x-[3px] gap-y-2.5 justify-center items-center">
          {/* Top level menu items */}
          {menuLinks?.map((item) => (
            <Link
              data-testid={DATA_TEST_IDS.LINK('nav')}
              key={item.name}
              className="px-5 xl:px-4 2xl:px-[28px] relative z-50"
              to={item.href ?? '#'}
              target={item.openInNewTab ? '_blank' : undefined}
              rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
              onMouseEnter={() => onMouseEnter(item)}
              onMouseLeave={onMouseLeave}
              onClick={() => onClick(item.name ?? 'undefined', item.href ?? '')}
            >
              <HeaderTextInternal key={item.name} {...item.text} />
              <div
                className={clsx('h-[2px] translate-y-2', {
                  'bg-blue-300': activeMenu && activeMenu.name === item.name,
                })}
              />
            </Link>
          ))}
        </nav>
      </div>
      {activeMenu && (
        <>
          <div className="fixed left-0 right-0 bottom-0 top-[185px] bg-black/80 z-30" />
          <div
            data-testid={DATA_TEST_IDS.ACTIVE_MENU}
            className="hidden xl:flex pt-2 pb-5 w-screen z-40 absolute left-0 right-0 top-[114px] bg-white"
            onMouseLeave={() => setActiveMenu(null)}
          >
            <PageSectionContainer className="flex justify-center xl:px-5 2xl:px-[97px] ">
              <div>
                <ul className="font-header text-20/26 font-bold space-y-5">
                  {activeMenu.megaMenuLinks?.leftLinks?.map((leftLink) => {
                    if (leftLink == null) return null;

                    return (
                      <li
                        data-testid={DATA_TEST_IDS.LEFT_LINK_SECTION}
                        key={leftLink?.title}
                        style={{
                          color: leftLink.color?.hex,
                        }}
                      >
                        <Link
                          data-testid={DATA_TEST_IDS.LINK('left-nav')}
                          to={leftLink.href ?? '#'}
                          className="hover:underline"
                          onClick={() =>
                            onClick(
                              leftLink.title ?? 'undefined',
                              leftLink.href ?? '',
                            )
                          }
                        >
                          {leftLink.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="flex ml-[70px] xl:space-x-[20px] 2xl:space-x-[42px] 3xl:space-x-[82px]">
                {activeMenu.megaMenuLinks?.columns.map((column, index) => (
                  <div
                    data-testid={DATA_TEST_IDS.MEGA_MENU_COLUMN}
                    className="w-[180px] space-y-[35px]"
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${column.name}-${index}`}
                  >
                    {column.groups?.map((group) => (
                      <div key={group?.title}>
                        <div className="font-header text-20/26 font-bold tracking-wider">
                          <Link
                            data-testid={DATA_TEST_IDS.LINK('column-nav')}
                            to={group?.titleLink ?? ''}
                            className="hover:underline"
                            onClick={() =>
                              onClick(
                                group?.name ?? 'undefined',
                                group?.titleLink ?? '',
                              )
                            }
                          >
                            {group?.title}
                          </Link>
                        </div>
                        {group?.items?.map((item, indexItem) => (
                          <div key={item?.name}>
                            {item?.title && (
                              <div
                                className={clsx(
                                  'font-header text-16/26 font-bold text-gray-400 tracking-wider',
                                  indexItem === 0 ? 'mt-[21px]' : 'mt-[25px]',
                                )}
                              >
                                {item?.title}
                              </div>
                            )}
                            <ul
                              className={clsx(
                                'space-y-4',
                                item?.title ? 'mt-2' : 'mt-[26px]',
                              )}
                            >
                              {item?.items?.map((subItem) => {
                                if (subItem == null) return null;

                                return (
                                  <li key={subItem.title} className="truncate">
                                    <Link
                                      data-testid={DATA_TEST_IDS.LINK(
                                        'item-nav',
                                      )}
                                      onClick={() =>
                                        onClick(
                                          subItem.title ?? 'undefined',
                                          subItem.href ?? '#',
                                        )
                                      }
                                      to={subItem.href ?? '#'}
                                      className="hover:underline"
                                      style={{
                                        color: subItem.color?.hex,
                                      }}
                                    >
                                      {subItem.title}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="xl:ml-[40px] 2xl:ml-[82px]">
                {activeMenu.megaMenuLinks?.image &&
                  activeMenu.megaMenuLinks?.image.asset && (
                    <div className="w-56 h-56">
                      <Link
                        data-testid={DATA_TEST_IDS.LINK('image-nav')}
                        to={activeMenu.megaMenuLinks?.link ?? ''}
                        onClick={() =>
                          onClick(
                            activeMenu.megaMenuLinks?.title ?? '',
                            activeMenu.megaMenuLinks?.link ?? '',
                          )
                        }
                      >
                        <SanityImage
                          dataset={sanityConfig.dataset}
                          layout="responsive"
                          objectFit="cover"
                          projectId={sanityConfig.projectId}
                          src={activeMenu.megaMenuLinks?.image.asset._ref}
                          breakpoints={[[224, 224]]}
                        />
                      </Link>
                    </div>
                  )}
                {activeMenu.megaMenuLinks?.title && (
                  <div className="text-20/26 font-header font-extrabold mt-2.5 w-56">
                    <Link
                      data-testid={DATA_TEST_IDS.LINK('megamenu-title-nav')}
                      to={activeMenu.megaMenuLinks?.link ?? ''}
                      className="hover:underline"
                      onClick={() =>
                        onClick(
                          activeMenu.megaMenuLinks?.title ?? '',
                          activeMenu.megaMenuLinks?.link ?? '',
                        )
                      }
                    >
                      {activeMenu.megaMenuLinks?.title}
                    </Link>
                  </div>
                )}
              </div>
            </PageSectionContainer>
          </div>
        </>
      )}
      <div className={'h-[60px] flex xl:hidden mx-[-20px] px-5 py-2.5'}>
        <SearchInputContainer
          placeholder={searchInputPlaceholder}
          featuredProducts={featuredProducts}
        />
      </div>
    </header>
  );
}

function CartBadge({className}: {className?: string}) {
  const {lines} = useCart();

  const linesWithoutAddonsAndBundleProducts = useProductCartItems(lines);

  const total = useMemo(
    () =>
      linesWithoutAddonsAndBundleProducts.reduce(
        (acc, line) => acc + line.quantity,
        0,
      ),
    [linesWithoutAddonsAndBundleProducts],
  );

  if (linesWithoutAddonsAndBundleProducts.length < 1) {
    return null;
  }

  return (
    <div
      data-testid={DATA_TEST_IDS.CART_BADGE}
      className={clsx(
        `text-contrast bg-primary absolute top-0 right-0 text-[0.625rem] font-medium subpixel-antialiased h-3 min-w-[0.75rem] flex items-center justify-center leading-none text-center rounded-full w-auto px-[0.125rem] pb-px p-[2px]`,
        className,
      )}
    >
      <span>{total}</span>
    </div>
  );
}
